import styled, { keyframes } from "styled-components";

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

export const FooterStyled = styled.footer`
  width: 100%;
  background: var(--bg-secondary);
  position: relative;
  overflow: hidden;

  /* Decorative background elements */
  .footerBg {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    overflow: hidden;

    .gradientOrb {
      position: absolute;
      border-radius: 50%;
      filter: blur(100px);

      &.orb1 {
        width: 400px;
        height: 400px;
        background: radial-gradient(
          circle,
          rgba(217, 58, 53, 0.08) 0%,
          transparent 70%
        );
        top: -100px;
        right: -100px;
      }

      &.orb2 {
        width: 300px;
        height: 300px;
        background: radial-gradient(
          circle,
          rgba(43, 59, 124, 0.08) 0%,
          transparent 70%
        );
        bottom: -100px;
        left: -50px;
      }
    }
  }

  .footerMain {
    max-width: 1400px;
    margin: 0 auto;
    padding: 80px 40px 60px;
    position: relative;
    z-index: 1;
  }

  .footerGrid {
    display: grid;
    grid-template-columns: 1.5fr 1fr 1fr 1.2fr;
    gap: 60px;
    margin-bottom: 60px;
  }

  .footerBrand {
    .brandLogo {
      height: 60px;
      width: auto;
      margin-bottom: 24px;
    }

    .brandDescription {
      font-family: Poppins;
      font-size: 15px;
      line-height: 1.8;
      color: var(--text-secondary);
      margin-bottom: 32px;
    }

    .socialLinks {
      display: flex;
      gap: 12px;

      a {
        width: 44px;
        height: 44px;
        border-radius: 12px;
        background: var(--card-bg);
        border: 2px solid var(--card-border);
        display: flex;
        align-items: center;
        justify-content: center;
        color: var(--text-primary);
        transition: all 0.3s ease;

        svg {
          width: 20px;
          height: 20px;
        }

        &:hover {
          background: var(--accent);
          border-color: var(--accent);
          color: #fff;
          transform: translateY(-4px);
        }
      }
    }
  }

  .footerLinks {
    h4 {
      font-family: Poppins;
      font-size: 18px;
      font-weight: 700;
      color: var(--text-primary);
      margin: 0 0 24px;
      position: relative;
      display: inline-block;

      &::after {
        content: "";
        position: absolute;
        bottom: -8px;
        left: 0;
        width: 30px;
        height: 3px;
        background: var(--accent);
        border-radius: 2px;
      }
    }

    ul {
      list-style: none;
      padding: 0;
      margin: 0;

      li {
        margin-bottom: 14px;

        a {
          font-family: Poppins;
          font-size: 15px;
          color: var(--text-secondary);
          text-decoration: none;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: all 0.3s ease;

          &::before {
            content: "";
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: var(--card-border);
            transition: all 0.3s ease;
          }

          &:hover {
            color: var(--accent);
            transform: translateX(8px);

            &::before {
              background: var(--accent);
            }
          }
        }
      }
    }
  }

  .footerContact {
    h4 {
      font-family: Poppins;
      font-size: 18px;
      font-weight: 700;
      color: var(--text-primary);
      margin: 0 0 24px;
      position: relative;
      display: inline-block;

      &::after {
        content: "";
        position: absolute;
        bottom: -8px;
        left: 0;
        width: 30px;
        height: 3px;
        background: var(--accent);
        border-radius: 2px;
      }
    }

    .contactItem {
      display: flex;
      align-items: flex-start;
      gap: 16px;
      margin-bottom: 20px;

      .contactIcon {
        width: 44px;
        height: 44px;
        border-radius: 12px;
        background: linear-gradient(
          135deg,
          rgba(217, 58, 53, 0.1) 0%,
          rgba(43, 59, 124, 0.1) 100%
        );
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        svg {
          width: 20px;
          height: 20px;
          color: var(--accent);
        }
      }

      .contactText {
        .contactLabel {
          font-family: Poppins;
          font-size: 13px;
          color: var(--text-secondary);
          margin-bottom: 4px;
        }

        .contactValue {
          font-family: Poppins;
          font-size: 15px;
          font-weight: 500;
          color: var(--text-primary);
        }

        a {
          color: var(--text-primary);
          text-decoration: none;
          transition: color 0.3s ease;

          &:hover {
            color: var(--accent);
          }
        }
      }
    }
  }

  .footerMap {
    background: var(--card-bg);
    border-radius: 20px;
    padding: 32px;
    border: 2px solid var(--card-border);

    h4 {
      font-family: Poppins;
      font-size: 18px;
      font-weight: 700;
      color: var(--text-primary);
      margin: 0 0 20px;
      position: relative;
      display: inline-block;

      &::after {
        content: "";
        position: absolute;
        bottom: -8px;
        left: 0;
        width: 30px;
        height: 3px;
        background: var(--accent);
        border-radius: 2px;
      }
    }

    .mapWrapper {
      display: block;
      position: relative;
      border-radius: 16px;
      overflow: hidden;
      border: 2px solid var(--card-border);
      transition: all 0.3s ease;

      &:hover {
        border-color: var(--accent);
        transform: translateY(-4px);
        box-shadow: 0 15px 40px var(--shadow-medium);

        .mapImage {
          transform: scale(1.05);
        }
      }

      .mapImage {
        width: 100%;
        height: 180px;
        object-fit: cover;
        display: block;
        transition: transform 0.5s ease;
      }

      .mapOverlay {
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 14px 18px;
        background: linear-gradient(
          180deg,
          transparent 0%,
          rgba(0, 0, 0, 0.75) 100%
        );
        display: flex;
        align-items: center;
        gap: 10px;

        svg {
          width: 18px;
          height: 18px;
          color: #fff;
        }

        span {
          font-family: Poppins;
          font-size: 14px;
          font-weight: 500;
          color: #fff;
        }
      }
    }

    .addressText {
      margin-top: 16px;
      font-family: Poppins;
      font-size: 14px;
      line-height: 1.6;
      color: var(--text-secondary);
    }
  }

  .footerBottom {
    border-top: 1px solid var(--card-border);
    padding: 30px 40px;
    max-width: 1400px;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    z-index: 1;

    .copyright {
      font-family: Poppins;
      font-size: 14px;
      color: var(--text-secondary);

      a {
        color: var(--accent);
        text-decoration: none;
        font-weight: 600;

        &:hover {
          text-decoration: underline;
        }
      }
    }

    .bottomLinks {
      display: flex;
      gap: 32px;

      a {
        font-family: Poppins;
        font-size: 14px;
        color: var(--text-secondary);
        text-decoration: none;
        transition: color 0.3s ease;

        &:hover {
          color: var(--accent);
        }
      }
    }
  }

  @media (max-width: 1024px) {
    .footerMain {
      padding: 60px 30px 50px;
    }

    .footerGrid {
      grid-template-columns: repeat(2, 1fr);
      gap: 40px;
    }

    .footerMap {
      grid-column: 1 / -1;
    }
  }

  @media (max-width: 768px) {
    .footerMain {
      padding: 50px 20px 40px;
    }

    .footerGrid {
      grid-template-columns: 1fr;
      gap: 40px;
    }

    .footerBrand {
      text-align: center;

      .socialLinks {
        justify-content: center;
      }
    }

    .footerLinks h4::after,
    .footerContact h4::after {
      left: 50%;
      transform: translateX(-50%);
    }

    .footerLinks,
    .footerContact {
      text-align: center;

      ul li a {
        justify-content: center;
      }

      .contactItem {
        justify-content: center;
      }
    }

    .footerMap {
      text-align: center;

      h4::after {
        left: 50%;
        transform: translateX(-50%);
      }
    }

    .footerBottom {
      flex-direction: column;
      gap: 20px;
      text-align: center;
      padding: 24px 20px;

      .bottomLinks {
        flex-wrap: wrap;
        justify-content: center;
        gap: 20px;
      }
    }
  }
`;
